import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import {
  EmailDynamicContent,
  EmailTemplate,
  Workflow,
  WorkflowDetails,
  WorkflowHistoryItem,
  WorkflowRun,
} from '@qtek/shared/models';
import { WorkflowActions } from './workflow.actions';
import { updateWorkflowTasks } from './workflow.utils';

export interface RoleParticipantsData {
  internalParticipants: Record<string, number>;
  externalParticipants: Record<string, number>;
  allParticipants: Record<string, number>;
}

export interface WorkflowState {
  allWorkflows: Record<string, Workflow[]>;
  activeWorkflow: WorkflowDetails | null;
  loading: boolean;
  allWorkflowsLoading: Record<string, boolean>;
  workflowRuns: Record<string, WorkflowRun[]>;
  workflowRunsLoading: Record<string, boolean>;
  workflowHistory: Record<string, WorkflowHistoryItem | null>;
  workflowHistoryLoading: Record<string, boolean>;
  emailDynamicContent: EmailDynamicContent | null;
  emailTemplates: EmailTemplate[];
  roleParticipantsByRefId: Record<string, RoleParticipantsData>;
  myDealsWorkflows: Workflow[];
  myDealsWorkflowsLoading: boolean;
  copyWorkflowsLoading: boolean;
  error: Error | null;
}

export const workflowInitialState: WorkflowState = {
  allWorkflows: {},
  activeWorkflow: null,
  loading: false,
  allWorkflowsLoading: {},
  workflowRuns: {},
  workflowRunsLoading: {},
  workflowHistory: {},
  workflowHistoryLoading: {},
  emailDynamicContent: null as WorkflowState['emailDynamicContent'],
  emailTemplates: [],
  roleParticipantsByRefId: {},
  myDealsWorkflows: [],
  myDealsWorkflowsLoading: false,
  copyWorkflowsLoading: false,
  error: null,
};

export const WorkflowReducer = createReducer(
  workflowInitialState,
  on(WorkflowActions.subscribeWorkflows, (state, { refId }) => ({
    ...state,
    loading: true,
    allWorkflowsLoading: {
      ...state.allWorkflowsLoading,
      [refId]: true,
    },
  })),
  on(WorkflowActions.unsubscribeWorkflows, (state, { refId }) => {
    const { [refId]: _, ...allWorkflows } = state.allWorkflows;
    const { [refId]: __, ...allWorkflowsLoading } = state.allWorkflowsLoading;

    return {
      ...state,
      allWorkflows,
      allWorkflowsLoading,
    };
  }),
  on(WorkflowActions.loadWorkflowsSuccess, (state, { workflows, refId }) => ({
    ...state,
    allWorkflows: {
      ...state.allWorkflows,
      [refId]: workflows.filter(wf => !wf.parentWorkflowId),
    },
    loading: false,
    allWorkflowsLoading: {
      ...state.allWorkflowsLoading,
      [refId]: false,
    },
    error: null as Error | null,
  })),
  on(WorkflowActions.loadWorkflowsFailure, (state, { error, refId }) => ({
    ...state,
    loading: false,
    allWorkflowsLoading: {
      ...state.allWorkflowsLoading,
      [refId]: false,
    },
    error,
  })),
  on(WorkflowActions.createWorkflow, (state, { refId }) => ({
    ...state,
    loading: true,
    allWorkflowsLoading: {
      ...state.allWorkflowsLoading,
      [refId]: true,
    },
  })),
  on(WorkflowActions.createWorkflowSuccess, (state, { workflow, refId }) => ({
    ...state,
    allWorkflows: {
      ...state.allWorkflows,
      [refId]: [workflow, ...(state.allWorkflows[refId] || [])],
    },
    loading: false,
    allWorkflowsLoading: { ...state.allWorkflowsLoading, [refId]: false },
    error: null as Error | null,
  })),
  on(WorkflowActions.createWorkflowFailure, (state, { error, refId }) => ({
    ...state,
    loading: false,
    allWorkflowsLoading: {
      ...state.allWorkflowsLoading,
      [refId]: false,
    },
    error,
  })),
  on(WorkflowActions.createSubWorkflow, (state, { refId }) => ({
    ...state,
    loading: true,
    allWorkflowsLoading: {
      ...state.allWorkflowsLoading,
      [refId]: true,
    },
  })),
  on(
    WorkflowActions.createSubWorkflowSuccess,
    (state, { subWorkflow, refId }) => {
      const active = state.activeWorkflow;
      if (!active || subWorkflow.parentWorkflowId !== active._id) {
        return {
          ...state,
          loading: false,
          allWorkflowsLoading: { ...state.allWorkflowsLoading, [refId]: false },
        };
      }
      return {
        ...state,
        loading: false,
        allWorkflowsLoading: { ...state.allWorkflowsLoading, [refId]: false },
        activeWorkflow: {
          ...active,
          subWorkflows: [...(active.subWorkflows || []), subWorkflow],
        },
        error: null as Error | null,
      };
    }
  ),
  on(WorkflowActions.createSubWorkflowFailure, (state, { error, refId }) => ({
    ...state,
    loading: false,
    allWorkflowsLoading: {
      ...state.allWorkflowsLoading,
      [refId]: false,
    },
    error,
  })),
  on(WorkflowActions.updateWorkflowSuccess, (state, { workflow, refId }) => {
    const existing = state.allWorkflows[refId] ?? [];
    return {
      ...state,
      loading: false,
      allWorkflows: {
        ...state.allWorkflows,
        [refId]: existing.map(w =>
          w._id === workflow._id ? { ...w, ...workflow } : w
        ),
      },
      error: null as Error | null,
    };
  }),
  on(WorkflowActions.updateWorkflowFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(WorkflowActions.deleteWorkflowSuccess, (state, { id, refId }) => {
    const fullRefId = refId.includes('itm=') ? refId : 'itm=' + refId;
    const active = state.activeWorkflow;
    const activeWorkflow = active?.subWorkflows?.some(sw => sw._id === id)
      ? {
          ...active,
          subWorkflows: active.subWorkflows.filter(sw => sw._id !== id),
        }
      : active;
    return {
      ...state,
      loading: false,
      activeWorkflow,
      allWorkflows: {
        ...state.allWorkflows,
        [fullRefId]: (state.allWorkflows[fullRefId] || []).filter(
          w => w._id !== id
        ),
      },
      error: null as Error | null,
    };
  }),
  on(WorkflowActions.deleteWorkflowFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(
    WorkflowActions.createWorkflowActionSuccess,
    (state, { task, workflowId }) => {
      const active = state.activeWorkflow;
      if (!active) {
        return { ...state };
      }
      const updatedActive =
        updateWorkflowTasks([active], workflowId, tasks => [
          ...tasks,
          task,
        ])[0] ?? null;
      return {
        ...state,
        activeWorkflow: updatedActive,
      };
    }
  ),
  on(
    WorkflowActions.updateWorkflowActionSuccess,
    (state, { task, workflowId, refId }) => {
      const activeWorkflow = state.activeWorkflow
        ? (updateWorkflowTasks([state.activeWorkflow], workflowId, tasks =>
            tasks.map(t => (t._id === task._id ? { ...t, ...task } : t))
          )[0] ?? null)
        : null;
      return {
        ...state,
        loading: false,
        activeWorkflow,
      };
    }
  ),
  on(WorkflowActions.updateWorkflowActionFailure, state => {
    return {
      ...state,
      loading: false,
    };
  }),
  on(
    WorkflowActions.removeWorkflowActionSuccess,
    (state, { taskId, workflowId, refId }) => {
      const removeTask = (tasks: any[]) => tasks.filter(t => t._id !== taskId);
      const activeWorkflow = state.activeWorkflow
        ? (updateWorkflowTasks(
            [state.activeWorkflow],
            workflowId,
            removeTask
          )[0] ?? null)
        : null;
      return {
        ...state,
        activeWorkflow,
      };
    }
  ),
  on(WorkflowActions.createTriggerSuccess, (state, { trigger }) => {
    const active = state.activeWorkflow;
    if (!active || trigger.workflowId !== active._id) {
      return { ...state };
    }
    return {
      ...state,
      activeWorkflow: {
        ...active,
        triggers: [...(active.triggers || []), trigger],
      },
    };
  }),
  on(WorkflowActions.updateTriggerSuccess, (state, { trigger }) => {
    const active = state.activeWorkflow;
    if (!active) {
      return { ...state };
    }
    const triggerIndex = (active.triggers || []).findIndex(
      t => t._id === trigger._id
    );
    if (triggerIndex === -1) {
      return { ...state };
    }
    const updatedTriggers = [...(active.triggers || [])];
    updatedTriggers[triggerIndex] = trigger;
    return {
      ...state,
      activeWorkflow: {
        ...active,
        triggers: updatedTriggers,
      },
    };
  }),
  on(WorkflowActions.deleteTriggerSuccess, (state, { trigger }) => {
    const active = state.activeWorkflow;
    if (!active) {
      return { ...state };
    }
    return {
      ...state,
      activeWorkflow: {
        ...active,
        triggers: (active.triggers || []).filter(t => t._id !== trigger._id),
      },
    };
  }),
  on(WorkflowActions.swapActions, state => ({
    ...state,
    loading: true,
  })),
  on(WorkflowActions.subscribeWorkflowHistory, (state, { workflowId }) => ({
    ...state,
    workflowRunsLoading: {
      ...state.workflowRunsLoading,
      [workflowId]: true,
    },
  })),
  on(WorkflowActions.unsubscribeWorkflowHistory, (state, { workflowId }) => {
    const { [workflowId]: _r, ...workflowRuns } = state.workflowRuns;
    const { [workflowId]: _rl, ...workflowRunsLoading } =
      state.workflowRunsLoading;

    const runIds = (state.workflowRuns[workflowId] ?? []).map(
      r => r.workflowStateId
    );
    const workflowHistory = { ...state.workflowHistory };
    const workflowHistoryLoading = { ...state.workflowHistoryLoading };
    runIds.forEach(runId => {
      delete workflowHistory[runId];
      delete workflowHistoryLoading[runId];
    });

    return {
      ...state,
      workflowRuns,
      workflowRunsLoading,
      workflowHistory,
      workflowHistoryLoading,
    };
  }),
  on(
    WorkflowActions.loadWorkflowRunsSuccess,
    (state, { runs, workflowId }) => ({
      ...state,
      workflowRuns: {
        ...state.workflowRuns,
        [workflowId]: [...runs].reverse(),
      },
      workflowRunsLoading: {
        ...state.workflowRunsLoading,
        [workflowId]: false,
      },
    })
  ),
  on(WorkflowActions.loadWorkflowRunsFailure, (state, { workflowId }) => ({
    ...state,
    workflowRunsLoading: {
      ...state.workflowRunsLoading,
      [workflowId]: false,
    },
  })),
  on(WorkflowActions.upsertWorkflowRun, (state, { workflowId, run }) => {
    const current = state.workflowRuns[workflowId] || [];
    const idx = current.findIndex(
      r => r.workflowStateId === run.workflowStateId
    );
    const next =
      idx >= 0
        ? current.map((r, n) => (n === idx ? run : r))
        : [...current, run];
    return {
      ...state,
      workflowRuns: {
        ...state.workflowRuns,
        [workflowId]: next,
      },
    };
  }),
  on(WorkflowActions.clearWorkflowHistory, (state, { runId }) => ({
    ...state,
    workflowHistory: { ...state.workflowHistory, [runId]: null } as Record<
      string,
      WorkflowHistoryItem | null
    >,
  })),
  on(WorkflowActions.getWorkflowHistoryByRun, (state, { runId }) => ({
    ...state,
    workflowHistoryLoading: {
      ...state.workflowHistoryLoading,
      [runId]: true,
    },
  })),
  on(
    WorkflowActions.loadWorkflowHistorySuccess,
    (state, { history, runId }) => ({
      ...state,
      workflowHistory: {
        ...state.workflowHistory,
        [runId]: history,
      },
      workflowHistoryLoading: {
        ...state.workflowHistoryLoading,
        [runId]: false,
      },
    })
  ),
  on(WorkflowActions.loadWorkflowHistoryFailure, (state, { runId }) => ({
    ...state,
    workflowHistoryLoading: {
      ...state.workflowHistoryLoading,
      [runId]: false,
    },
  })),
  on(
    WorkflowActions.getEmailDynamicContentSuccess,
    (state, { dynamicContent }) => ({
      ...state,
      emailDynamicContent: dynamicContent,
    })
  ),
  on(WorkflowActions.getEmailDynamicContentFailure, state => ({
    ...state,
    emailDynamicContent: null as EmailDynamicContent | null,
  })),
  on(WorkflowActions.getEmailTemplatesSuccess, (state, { templates }) => ({
    ...state,
    emailTemplates: templates,
  })),
  on(WorkflowActions.getEmailTemplatesFailure, state => ({
    ...state,
    emailTemplates: [] as EmailTemplate[],
  })),
  on(
    WorkflowActions.loadRoleParticipantsSuccess,
    (state, { refId, participants }) => ({
      ...state,
      roleParticipantsByRefId: {
        ...state.roleParticipantsByRefId,
        [refId]: participants,
      },
    })
  ),
  on(WorkflowActions.clearRoleParticipants, (state, { refId }) => {
    const { [refId]: _, ...roleParticipantsByRefId } =
      state.roleParticipantsByRefId;
    return { ...state, roleParticipantsByRefId };
  }),
  on(WorkflowActions.getWorkflowSuccess, (state, { workflow, refId }) => {
    return {
      ...state,
      activeWorkflow: workflow,
    };
  }),
  on(WorkflowActions.setActiveWorkflow, (state, { workflow }) => ({
    ...state,
    activeWorkflow: workflow,
  })),
  on(WorkflowActions.subscribeMyDealsWorkflows, state => ({
    ...state,
    myDealsWorkflowsLoading: true,
  })),
  on(WorkflowActions.unsubscribeMyDealsWorkflows, state => ({
    ...state,
    myDealsWorkflows: [] as Workflow[],
    myDealsWorkflowsLoading: false,
  })),
  on(WorkflowActions.loadMyDealsWorkflowsSuccess, (state, { workflows }) => ({
    ...state,
    myDealsWorkflows: workflows.filter(w => !w.parentWorkflowId),
    myDealsWorkflowsLoading: false,
    error: null as Error | null,
  })),
  on(WorkflowActions.loadMyDealsWorkflowsFailure, (state, { error }) => ({
    ...state,
    myDealsWorkflowsLoading: false,
    error,
  })),
  on(WorkflowActions.copyWorkflows, state => ({
    ...state,
    copyWorkflowsLoading: true,
  })),
  on(WorkflowActions.copyWorkflowsSuccess, state => ({
    ...state,
    copyWorkflowsLoading: false,
    error: null as Error | null,
  })),
  on(WorkflowActions.copyWorkflowsFailure, (state, { error }) => ({
    ...state,
    copyWorkflowsLoading: false,
    error,
  }))
);

export const WorkflowFeature = createFeature({
  name: 'workflow',
  reducer: WorkflowReducer,
  extraSelectors: ({ selectWorkflowState }) => ({
    selectWorkflowsByRefId: (refId: string) =>
      createSelector(
        selectWorkflowState,
        (state: WorkflowState) => state?.allWorkflows?.[refId] || []
      ),
    selectWorkflowsLoadingByRefId: (refId: string) =>
      createSelector(
        selectWorkflowState,
        (state: WorkflowState) => state?.allWorkflowsLoading?.[refId] || false
      ),
    selectWorkflowById: (refId: string, workflowId: string) =>
      createSelector(selectWorkflowState, (state: WorkflowState) => {
        const workflows = state?.allWorkflows?.[refId] || [];
        return workflows.find(w => w._id === workflowId) || null;
      }),
    selectWorkflowRunsByWorkflowId: (workflowId: string) =>
      createSelector(
        selectWorkflowState,
        (state: WorkflowState) => state?.workflowRuns?.[workflowId] || []
      ),
    selectWorkflowRunsLoadingByWorkflowId: (workflowId: string) =>
      createSelector(
        selectWorkflowState,
        (state: WorkflowState) =>
          state?.workflowRunsLoading?.[workflowId] || false
      ),
    selectWorkflowHistoryByRunId: (runId: string) =>
      createSelector(
        selectWorkflowState,
        (state: WorkflowState) => state?.workflowHistory?.[runId] ?? null
      ),
    selectWorkflowHistoryLoadingByRunId: (runId: string) =>
      createSelector(
        selectWorkflowState,
        (state: WorkflowState) =>
          state?.workflowHistoryLoading?.[runId] || false
      ),
    selectEmailDynamicContent: createSelector(
      selectWorkflowState,
      (state: WorkflowState) => state?.emailDynamicContent
    ),
    selectEmailTemplates: createSelector(
      selectWorkflowState,
      (state: WorkflowState) => state?.emailTemplates
    ),
    selectRoleParticipantsByRefId: (refId: string) =>
      createSelector(
        selectWorkflowState,
        (state: WorkflowState) =>
          state?.roleParticipantsByRefId?.[refId] ?? null
      ),
    selectMyDealsWorkflows: createSelector(
      selectWorkflowState,
      (state: WorkflowState) => state?.myDealsWorkflows ?? []
    ),
    selectMyDealsWorkflowsLoading: createSelector(
      selectWorkflowState,
      (state: WorkflowState) => state?.myDealsWorkflowsLoading ?? false
    ),
    selectCopyWorkflowsLoading: createSelector(
      selectWorkflowState,
      (state: WorkflowState) => state?.copyWorkflowsLoading ?? false
    ),
    selectMyDealsWorkflowsGrouped: createSelector(
      selectWorkflowState,
      (state: WorkflowState) => {
        // Only workflows with at least one task are copy-eligible (BE rejects
        // empty-workflow copies). Treat undefined `hasTasks` as not-eligible
        // for safety — pickers should require the BE to explicitly say yes.
        const workflows = (state?.myDealsWorkflows ?? []).filter(
          w => w.hasTasks === true
        );
        const groups = new Map<string, Workflow[]>();
        for (const w of workflows) {
          const key = w.dealName || '';
          const bucket = groups.get(key);
          if (bucket) {
            bucket.push(w);
          } else {
            groups.set(key, [w]);
          }
        }
        return Array.from(groups, ([dealName, workflowsForDeal]) => ({
          dealName,
          workflows: workflowsForDeal,
        }));
      }
    ),
  }),
});

export const {
  selectAllWorkflows,
  selectActiveWorkflow,
  selectLoading,
  selectAllWorkflowsLoading,
  selectError,
  selectWorkflowsByRefId,
  selectWorkflowsLoadingByRefId,
  selectWorkflowById,
  selectWorkflowRunsByWorkflowId,
  selectWorkflowRunsLoadingByWorkflowId,
  selectWorkflowHistoryByRunId,
  selectWorkflowHistoryLoadingByRunId,
  selectEmailDynamicContent,
  selectEmailTemplates,
  selectRoleParticipantsByRefId,
  selectMyDealsWorkflows,
  selectMyDealsWorkflowsLoading,
  selectCopyWorkflowsLoading,
  selectMyDealsWorkflowsGrouped,
} = WorkflowFeature;
